import { PayloadAction } from '@reduxjs/toolkit';
import { put, call, takeEvery } from 'typed-redux-saga';
import { toast } from 'react-toastify';

import ApprovalGateway from '~apis/approval';
import combinedActions from '~redux/combinedActions';
import { store } from '~redux/store';
import { TupdateTransferStatusReduxParams } from '~types/feat/approval';

import { GatewayResponseStatus } from '~types/gateway';
import { SagaWatcherReturnType } from '~types/saga';

import { ErrorMessages, SuccessMessages } from '~utils/constants';
import { TransferStatusEnum } from '~utils/enums';

export default function* watchUpdateTransferStatus(api: ApprovalGateway): SagaWatcherReturnType {
    yield takeEvery('approval/approvalupdateTransferStatusAttempt', handleUpdateTransferStatus, api);
}

function getNextStatus(currentStatus: TransferStatusEnum, requestedStatus: TransferStatusEnum): TransferStatusEnum {
    // Approve flow
    if (requestedStatus === TransferStatusEnum.Approved) {
        if (currentStatus === TransferStatusEnum.PendingDeliver) return TransferStatusEnum.PendingReceive;
        if (currentStatus === TransferStatusEnum.PendingReceive) return TransferStatusEnum.Approved;
    }

    // Reject flow
    if (requestedStatus === TransferStatusEnum.RejectedDeliver && currentStatus === TransferStatusEnum.PendingDeliver) {
        return TransferStatusEnum.RejectedDeliver;
    }
    if (requestedStatus === TransferStatusEnum.RejectedReceive && currentStatus === TransferStatusEnum.PendingReceive) {
        return TransferStatusEnum.RejectedReceive;
    }

    return currentStatus;
}

function* handleUpdateTransferStatus(api: ApprovalGateway, data: PayloadAction<TupdateTransferStatusReduxParams>) {
    const reduxState = store.getState();
    const authToken = reduxState.auth.userDetails.authToken;
    const { id, status: requestedStatus } = data.payload;

    const pendingTransferListing = reduxState.approval.pendingTransferListing.data;
    const oldStatus = pendingTransferListing.find(item => item.transferId === id)?.status as TransferStatusEnum;

    const nextStatus = getNextStatus(oldStatus, requestedStatus);

    const response = yield* call([api, api.updateInventoryTransferStatus], {
        authToken,
        id,
        status: nextStatus,
    });

    if (response.status === GatewayResponseStatus.Error) {
        yield put(combinedActions.approvalupdateTransferStatusFailure(response.message || ErrorMessages.GenericError));
        toast.error(response.message || ErrorMessages.GenericError);
        return;
    }

    const successMessage =
        nextStatus === TransferStatusEnum.Approved
            ? SuccessMessages.ApprovalApproveTransferSuccess
            : nextStatus === TransferStatusEnum.PendingReceive
                ? SuccessMessages.ApprovalPendingTransferSuccess
                : nextStatus === TransferStatusEnum.RejectedDeliver || nextStatus === TransferStatusEnum.RejectedReceive
                    ? SuccessMessages.ApprovalRejectTransferSuccess
                    : '';

    yield put(combinedActions.approvalSetConfirmupdateTransferStatusModalIsOpen(false));
    toast.success(successMessage);

    if (nextStatus === oldStatus) {
        yield put(combinedActions.approvalupdateTransferStatusSuccess());
        return;
    }

    const pendingTransferListingIndex = reduxState.approval.pendingTransferListing.index;
    if (pendingTransferListing.length === 1) {
        yield put(
            combinedActions.approvalSetpendingTransferListingIndex({
                index: pendingTransferListingIndex > 0 ? pendingTransferListingIndex - 1 : 0,
            })
        );
    }

    yield put(combinedActions.approvalGetPendingTransferListingAttempt());
    yield put(combinedActions.approvalupdateTransferStatusSuccess());
}
